import type { AppServices } from '../app';
import type { Selection } from '../state';
import { hasClip } from '../audio/narrator';
import { factKey, nameClip } from './narration';

/** The pre-rendered clip that says the name of a selection, if there is one. */
export function nameClipKey(s: AppServices, sel: Selection): string | null {
  const key = candidateKey(s, sel);
  return key && hasClip(key) ? key : null;
}

/** The clips "Listen" plays for a selection: its spoken facts. Never an Arabic name: the Arabic is heard only from the
 *  speaker beside it, in the Arabic voice (BUILD-SPEC rule G9). */
export function factClipKeys(s: AppServices, sel: Selection): string[] {
  let key: string | null = null;
  switch (sel.kind) {
    case 'body': key = factKey.body(sel.id); break;
    case 'star': {
      const info = s.engine.stars.info.get(sel.index);
      if (info?.name) key = factKey.star(info.name);
      break;
    }
    case 'constellation': key = factKey.constellation(s.engine.cons.constellations[sel.index].id); break;
    case 'dso': {
      const d = s.engine.dsos[sel.index];
      if (d.m) key = factKey.messier(d.m);
      break;
    }
    case 'satellite': key = factKey.satellite(satKind(s, sel.id)); break;
    case 'shower': key = factKey.shower(sel.code); break;
  }
  return key && hasClip(key) ? [key] : [];
}

const satKind = (s: AppServices, id: number) =>
  id === 25544 ? 'iss' : id === 48274 ? 'tiangong' : id === 20580 ? 'hubble' : s.sats.meta.get(id)?.kind === 'starlink' ? 'starlink' : 'other';

function candidateKey(s: AppServices, sel: Selection): string | null {
  switch (sel.kind) {
    case 'body':
      return nameClip.body(sel.id).key;
    case 'star': {
      const info = s.engine.stars.info.get(sel.index);
      return info?.name ? nameClip.star(info.name, '').key : null;
    }
    case 'constellation': {
      const c = s.engine.cons.constellations[sel.index];
      return nameClip.constellation(c.id, c.name, '').key;
    }
    case 'dso': {
      const d = s.engine.dsos[sel.index];
      return d.m ? nameClip.messier(d.m, d.name, d.t, '').key : null;
    }
    case 'satellite':
      return nameClip.satellite(satKind(s, sel.id)).key;
    case 'shower':
      return nameClip.shower(sel.code, '').key;
    default:
      return null;
  }
}
